import React, { useEffect, useState } from "react";
import { useRef } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { getFormMode } from "../../../../lib/helpers";
import { formatDeleteErrorMessage } from "../../../../GlobalFunction/ErrorMessageHelper";
import { object, z } from "zod";
import {
  Checkbox,
  FormLabel,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import {
  insertMessPlan,
  retriveMessPlan,
  updateMessPlan,
} from "../../../../Api/MessPlanMst/Api";
import CrudButtons from "../../../../GlobalFunction/CrudButtons";
import { useReactToPrint } from "react-to-print";
import ReportTable from "../../../../atoms/ReportFolder/ReportTable";
import axiosInstance from "../../../../lib/axios";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";

const columns = [
  { id: "RowNum", label: "S.No", minWidth: 170 },
  { id: "MessPlanName", label: "Messplan", minWidth: 100 },
  { id: "Descrition", label: "Discription", minWidth: 100 },
  { id: "Amount", label: "Amount", minWidth: 100 },
  { id: "Remark", label: "Remark", minWidth: 100 },
];

const FORM_MODE = getFormMode();
const FORM_INIT_STATE = {
  MessPlanName: "",
  Amount: "",
  Descrition: "",
  Remark: "",
  Status: "1",
  IsSplit: "Y", // Initialize IsSplit with default value 'Y'
};

const messFormSchema = z.object({
  MessPlanName: z.string().min(1, "Mess Plan Name is required"),
  Amount: z.string().min(1, "Amount is required"),
  Descrition: z.string().optional(),
  Remark: z.string().optional(),
  Status: z.enum(["1", "0"]).optional(),
  IsSplit: z.enum(["Y", "N"]).optional(),
});

const MessPlan = () => {
  const [UserId] = useState(sessionStorage.getItem("UserId"));
  const [dataForPrint, setDataForPrint] = useState({});
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(FORM_INIT_STATE);
  const [currentMessPlanID, setCurrentMessPlanID] = useState(() => {
    return location?.state?.MessPlanID;
  });
  const [mode, setMode] = useState(() =>
    currentMessPlanID ? FORM_MODE.read : FORM_MODE.add
  );
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [Status, setStatus] = useState("1");
  const [IsSplit, setIsSplit] = useState("Y");

  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({ contentRef });

  // Handle Status Checkbox
  const handleChangeStatus = (event) => {
    const updatedStatus = event.target.checked ? "1" : "0";
    setStatus(updatedStatus);
    setForm((prevData) => ({
      ...prevData,
      Status: updatedStatus,
    }));
  };

  // Handle IsSplit Radio Button Change
  const handleSplitChange = (event) => {
    const newValue = event.target.value;
    setIsSplit(newValue);
    setForm((prevData) => ({
      ...prevData,
      IsSplit: newValue,
    }));
  };

  // Fetch Dashboard Data for Printing
  useEffect(() => {
    const getRow = async () => {
      const params = {
        start: 0,
        PageSize: 10,
        SearchText: "",
      };
      try {
        const response = await axiosInstance.post(
          "MstMessPlan/GetMstMessPlanDashBoard",
          { params }
        );
        if (response.data && response.data.Data) {
          setRows(response.data.Data);
          setDataForPrint(response.data.Data);
        } else {
          console.log("No records found in the dashboard api response");
        }
      } catch (error) {
        console.error("Error while fetching data for printing", error);
      }
    };
    getRow();
  }, []);

  // Fetch Mess Plan Data
  useEffect(() => {
    if (currentMessPlanID) {
      fetchMessPlanData();
    }
  }, []);

  const handlePrevious = async () => {
    if (!currentMessPlanID && currentMessPlanID < 1) return;
    await fetchMessPlanData("P");
  };

  const handleNext = async () => {
    if (!currentMessPlanID) return;
    await fetchMessPlanData("N");
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
    }
  };

  const handleEdit = async () => {
    setMode(FORM_MODE.edit);
  };

  const handleAdd = () => {
    setMode(FORM_MODE.add);
    setForm(FORM_INIT_STATE);
    setIsSplit("Y");
  };

  const handleCancel = async (flag = "L") => {
    if (mode === FORM_MODE.add) {
      await fetchMessPlanData("L", 1);
    } else {
      await fetchMessPlanData("R");
    }
    setMode(FORM_MODE.read);
  };

  const handleExit = () => {
    navigate(`/mess-plan-table`);
  };

  const handleDelete = async () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmDelete = async () => {
    setOpenConfirmDialog(false);
    try {
      await fetchMessPlanData("D");
      const response = await retriveMessPlan({
        MessPlanID: currentMessPlanID,
        Flag: "D",
      });
      if (response instanceof Error) {
        return toast.error("Error: could not delete room");
      }
      const {
        data: { Status, Message, Data },
      } = response;
      if (Status === 0) {
        toast.success(Message, { autoClose: 1000 });
      } else {
        if (Array.isArray(Data) && Data.length > 0) {
          const msg = formatDeleteErrorMessage(Data);
          toast.error(msg, { autoClose: 1000 });
        } else {
          toast.error(Message, { autoClose: 1000 });
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMessPlanData = async (flag = "R", MessPlanID = 1) => {
    const response = await retriveMessPlan({
      MessPlanID: currentMessPlanID ?? MessPlanID,
      Flag: flag,
    });
    if (response instanceof Error) {
      return console.log(response.message);
    }
    const {
      data: { Status, responseStatusCode, Data, Message },
    } = response;
    if (
      Status === 0 &&
      responseStatusCode === 1 &&
      Data &&
      Array.isArray(Data)
    ) {
      const data = Data[0];
      setForm({
        MessPlanName: data.MessPlanName,
        Amount: data.Amount ? String(data.Amount) : "",
        Descrition: data.Descrition,
        Remark: data.Remark,
        Status: data.Status,
        IsSplit: data.IsSplit ?? "Y",
      });
      setStatus(data.Status?.toString() ?? "1");
      setIsSplit(data.IsSplit ?? "Y");
      setMode(FORM_MODE.read);
      setCurrentMessPlanID(data.MessPlanID);
    } else {
      console.log(Message);
    }
  };

  const handleSubmit = async () => {
    const result = messFormSchema.safeParse(form);

    if (!result.success) {
      return toast.info("Please fill in all required inputs correctly.", {
        autoClose: 500,
      });
    }
    const { data } = result;

    const payload = {
      MessPlanName: data.MessPlanName,
      Amount: data.Amount ? data.Amount : 0,
      PytType: "0",
      Descrition: data.Descrition,
      Remark: data.Remark,
      Status: data.Status,
      IsSplit: data.IsSplit,
    };

    let response;
    if (mode === FORM_MODE.edit && currentMessPlanID) {
      payload.MessPlanID = currentMessPlanID;
      payload.UpdatedBy = UserId;
      response = await updateMessPlan(payload);
    } else {
      payload.CreatedBy = UserId;
      response = await insertMessPlan(payload);
    }

    if (response instanceof Error) {
      toast.error("Error: Could not submit form");
    }
    const {
      data: { Status, Message },
    } = response;
    if (Status === 0) {
      setMode(FORM_MODE.read);
      toast.success(Message, {
        autoClose: 1000,
      });
    } else {
      console.error(Message);
    }
  };

  return (
    <>
      <ToastContainer />
      <Box className="form-container">
        <Grid container spacing={2} className="form_grid">
          <Grid
            item
            xs={12}
            className="form_title"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "20px",
              flexDirection: { xs: "column", sm: "row", md: "row" },
            }}
          >
            <Grid
              sx={{
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-start" },
                marginBottom: { xs: "10px", sm: 0 },
              }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#635bff",
                  marginRight: { xs: "5px", sm: "10px" },
                  display: { xs: "none", sm: "block" },
                }}
                className="three-d-button-previous"
                onClick={handlePrevious}
                disabled={
                  mode !== FORM_MODE.read ||
                  !currentMessPlanID ||
                  currentMessPlanID === 1
                }
              >
                <KeyboardArrowLeftIcon />
              </Button>

              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#635bff",
                  display: { xs: "none", sm: "block" },
                }}
                className="three-d-button-next"
                onClick={handleNext}
                disabled={mode !== FORM_MODE.read}
              >
                <NavigateNextIcon />
              </Button>
            </Grid>
            <Typography
              variant="h5"
              sx={{
                margin: "Auto",
                textAlign: { xs: "center", sm: "center" },
                fontSize: { xs: "24px", sm: "18px", md: "24px" },
              }}
            >
              Addon2 Master
            </Typography>
            <Grid sx={{ display: "flex", justifyContent: "end" }}>
              <CrudButtons
                moduleName="Addon2"
                mode={mode}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onView={handlePrint}
                onDelete={handleDelete}
                onExit={handleExit}
                onPrevious={handlePrevious}
                onNext={handleNext}
                readOnlyMode={mode === FORM_MODE.read}
              />
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            className="form_field"
            sx={{ marginInline: { xs: "10px", sm: "80px" } }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      id="MessPlanName"
                      name="MessPlanName"
                      label={
                        <span>
                          Plan Name
                          <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="filled"
                      fullWidth
                      className="custom-textfield"
                      value={form.MessPlanName}
                      onChange={handleInputChange}
                      disabled={mode === FORM_MODE.read}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      id="amount"
                      name="Amount"
                      label={
                        <span>
                          Amount<span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="filled"
                      fullWidth
                      className="custom-textfield"
                      value={form.Amount}
                      onChange={handleInputChange}
                      disabled={mode === FORM_MODE.read}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      id="description"
                      name="Descrition"
                      label="Description"
                      variant="filled"
                      fullWidth
                      className="custom-textfield"
                      value={form.Descrition}
                      onChange={handleInputChange}
                      disabled={mode === FORM_MODE.read}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      id="remark"
                      name="Remark"
                      label="Remark"
                      variant="filled"
                      fullWidth
                      className="custom-textfield"
                      value={form.Remark}
                      onChange={handleInputChange}
                      disabled={mode === FORM_MODE.read}
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FormLabel
                        component="legend"
                      >
                        Split
                      </FormLabel>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          name="IsSplit"
                          value={IsSplit}
                          onChange={handleSplitChange}
                          disabled={mode === FORM_MODE.read}
                        >
                          <FormControlLabel
                            value="Y"
                            control={<Radio size="small" />}
                            label="Yes"
                            className="custom-textfield"
                            disabled={mode === FORM_MODE.read}
                          />
                          <FormControlLabel
                            electron

                            value="N"
                            control={<Radio size="small" />}
                            label="No"
                            className="custom-textfield"
                            disabled={mode === FORM_MODE.read}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={mode === FORM_MODE.read}
                            checked={Status === "1"}
                            onChange={handleChangeStatus}
                          />
                        }
                        label="Status"
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className="form_button">
            {mode === FORM_MODE.read && (
              <>
                <Button
                  variant="contained"
                  sx={{
                    mr: 1,
                    background: "linear-gradient(290deg, #d4d4d4, #ffffff)",
                  }}
                  onClick={handleSubmit}
                  disabled
                >
                  Submit
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    mr: 1,
                    background: "linear-gradient(290deg, #a7c5e9, #ffffff)",
                  }}
                  onClick={handleCancel}
                  disabled
                >
                  Cancel
                </Button>
              </>
            )}
            {(mode === FORM_MODE.edit || mode === FORM_MODE.add) && (
              <>
                <Button
                  variant="contained"
                  sx={{
                    mr: 1,
                    background: "linear-gradient(290deg, #b9d0e9, #e9f2fa)",
                  }}
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    mr: 1,
                    background: "linear-gradient(290deg, #b9d0e9, #e9f2fa)",
                  }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Box>

      <div style={{ display: "none" }}>
        {Object.keys(dataForPrint).length > 0 && (
          <ReportTable
            columns={columns}
            rows={rows}
            title="Addon2 Records"
            contentRef={contentRef}
            customStyles={{ backgroundColor: "#f5f5f5" }}
          />
        )}
      </div>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              backgroundColor: "#635bff",
              color: "white",
              "&:hover": {
                backgroundColor: "#1565c0",
                color: "white",
              },
            }}
            onClick={handleConfirmDelete}
          >
            Yes
          </Button>
          <Button
            sx={{
              backgroundColor: "#635bff",
              color: "white",
              "&:hover": {
                backgroundColor: "#1565c0",
                color: "white",
              },
            }}
            onClick={handleCloseConfirmDialog}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MessPlan;